<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import MigrationStatusBanner from './MigrationStatusBanner.vue';

/* global axios */

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const isLoading = ref(false);
const stats = ref(null);
const compatibleInboxes = ref([]);
const selectedTargetInboxId = ref(null);
const isFetchingStats = ref(false);
const isFetchingInboxes = ref(false);
const isExpanded = ref(false);

// Check if inbox is API type
const isApiInbox = computed(() => {
  return props.inbox?.channel_type === 'Channel::Api';
});

// Fetch statistics
const fetchStats = async () => {
  try {
    isFetchingStats.value = true;
    
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/migrate_conversations/stats`
    );
    
    stats.value = response.data;
  } catch (error) {
    console.error('[MIGRATE-CONVERSATIONS] Error fetching stats:', error);
    useAlert(t('MIGRATE_CONVERSATIONS.MESSAGES.LOAD_ERROR'));
  } finally {
    isFetchingStats.value = false;
  }
};

// Fetch compatible inboxes
const fetchCompatibleInboxes = async () => {
  try {
    isFetchingInboxes.value = true;
    
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/migrate_conversations/compatible_inboxes`
    );
    
    compatibleInboxes.value = response.data.compatible_inboxes || [];
  } catch (error) {
    console.error('[MIGRATE-CONVERSATIONS] Error fetching compatible inboxes:', error);
    useAlert(t('MIGRATE_CONVERSATIONS.MESSAGES.LOAD_ERROR'));
  } finally {
    isFetchingInboxes.value = false;
  }
};

// Start migration
const startMigration = async () => {
  if (!selectedTargetInboxId.value) {
    useAlert(t('MIGRATE_CONVERSATIONS.MESSAGES.TARGET_REQUIRED'));
    return;
  }

  if (selectedTargetInboxId.value === props.inbox.id) {
    useAlert(t('MIGRATE_CONVERSATIONS.MESSAGES.SAME_INBOX_ERROR'));
    return;
  }

  const targetInbox = compatibleInboxes.value.find(i => i.id === selectedTargetInboxId.value);

  if (!confirm(t('MIGRATE_CONVERSATIONS.ACTIONS.CONFIRM_MESSAGE'))) {
    return;
  }

  try {
    isLoading.value = true;

    await axios.post(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inbox.id}/migrate_conversations/migrate`,
      {
        target_inbox_id: selectedTargetInboxId.value
      }
    );

    useAlert(t('MIGRATE_CONVERSATIONS.MESSAGES.MIGRATION_STARTED'));
    
    // Refresh stats
    await fetchStats();
  } catch (error) {
    console.error('[MIGRATE-CONVERSATIONS] Error starting migration:', error);
    useAlert(t('MIGRATE_CONVERSATIONS.MESSAGES.MIGRATION_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const hasData = computed(() => {
  // Show section only if there are conversations or messages to migrate
  return stats.value && (
    stats.value.conversations_count > 0 ||
    stats.value.messages_count > 0
  );
});

const loadData = async () => {
  if (isApiInbox.value && props.inbox?.id) {
    await Promise.all([
      fetchStats(),
      fetchCompatibleInboxes()
    ]);
  }
};

// Public method to refresh data (called when migration completes)
const refreshData = async () => {
  await loadData();
};

// Expose refreshData for parent components
defineExpose({ refreshData });

onMounted(async () => {
  await loadData();
});

// Watch for inbox changes to reload data when navigating between inboxes
watch(
  () => props.inbox?.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      await loadData();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="isApiInbox && hasData" class="mt-6 pt-6 border-t border-n-slate-6">
    <button
      class="w-full flex items-center justify-between mb-4 text-left group"
      @click="isExpanded = !isExpanded"
    >
      <div class="flex-1">
        <h3 class="text-base font-medium text-n-slate-12 group-hover:text-n-brand transition-colors">
          {{ $t('MIGRATE_CONVERSATIONS.TITLE') }}
        </h3>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('MIGRATE_CONVERSATIONS.DESCRIPTION') }}
        </p>
      </div>
      <span class="text-n-slate-10 ml-4 transition-transform" :class="{ 'rotate-180': isExpanded }">
        ▼
      </span>
    </button>

    <div v-show="isExpanded" class="space-y-4">

    <!-- Migration Status Banner -->
    <MigrationStatusBanner 
      v-if="inbox && inbox.id" 
      :inbox-id="inbox.id" 
      @migration-completed="loadData" 
    />

    <!-- Warning -->
    <div class="rounded-lg border border-yellow-200 bg-yellow-50 p-4 mb-4">
      <div class="flex items-start gap-3">
        <span class="text-xl">⚠️</span>
        <div class="flex-1">
          <h4 class="text-sm font-semibold text-yellow-900 mb-2">
            {{ $t('MIGRATE_CONVERSATIONS.WARNING.TITLE') }}
          </h4>
          <ul class="text-sm text-yellow-800 space-y-1.5 ml-0 list-none">
            <li class="flex items-start gap-2">
              <span class="text-yellow-600 mt-0.5">•</span>
              <span>{{ $t('MIGRATE_CONVERSATIONS.WARNING.BACKUP') }}</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-yellow-600 mt-0.5">•</span>
              <span>{{ $t('MIGRATE_CONVERSATIONS.WARNING.IRREVERSIBLE') }}</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-yellow-600 mt-0.5">•</span>
              <span>{{ $t('MIGRATE_CONVERSATIONS.WARNING.DOWNTIME') }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Loading stats -->
    <div v-if="isFetchingStats" class="flex items-center gap-2 text-sm text-n-slate-11 mb-4">
      <span class="animate-spin">⏳</span>
      Loading statistics...
    </div>

    <!-- Stats display -->
    <div v-else-if="stats" class="space-y-4">
      <!-- Stats card -->
      <div class="p-4 bg-n-slate-2 border border-n-slate-6 rounded-lg">
        <h4 class="text-sm font-medium text-n-slate-12 mb-3">
          {{ $t('MIGRATE_CONVERSATIONS.STATS.TITLE') }}
        </h4>
        <div class="grid grid-cols-3 gap-4">
          <div>
            <div class="text-xs text-n-slate-11">
              {{ $t('MIGRATE_CONVERSATIONS.STATS.CONVERSATIONS') }}
            </div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ stats.conversations_count || 0 }}
            </div>
          </div>
          <div>
            <div class="text-xs text-n-slate-11">
              {{ $t('MIGRATE_CONVERSATIONS.STATS.MESSAGES') }}
            </div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ stats.messages_count || 0 }}
            </div>
          </div>
          <div>
            <div class="text-xs text-n-slate-11">
              {{ $t('MIGRATE_CONVERSATIONS.STATS.CONTACTS') }}
            </div>
            <div class="text-2xl font-semibold text-n-slate-12">
              {{ stats.contacts_count || 0 }}
            </div>
          </div>
        </div>
      </div>

      <!-- Target inbox selection -->
      <div v-if="hasData" class="space-y-3">
        <div>
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ $t('MIGRATE_CONVERSATIONS.TARGET.LABEL') }}
          </label>
          
          <div v-if="isFetchingInboxes" class="text-sm text-n-slate-11">
            Loading compatible inboxes...
          </div>
          
          <div v-else-if="compatibleInboxes.length === 0" class="text-sm text-n-slate-11">
            {{ $t('MIGRATE_CONVERSATIONS.TARGET.NO_COMPATIBLE') }}
          </div>
          
          <select
            v-else
            v-model="selectedTargetInboxId"
            class="w-full px-3 py-2 border border-n-slate-7 rounded-lg bg-white text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
          >
            <option :value="null">{{ $t('MIGRATE_CONVERSATIONS.TARGET.PLACEHOLDER') }}</option>
            <option
              v-for="targetInbox in compatibleInboxes"
              :key="targetInbox.id"
              :value="targetInbox.id"
            >
              {{ targetInbox.name }} ({{ targetInbox.conversations_count }} conversations)
            </option>
          </select>
          
          <p class="mt-1 text-xs text-n-slate-11">
            {{ $t('MIGRATE_CONVERSATIONS.TARGET.HELP') }}
          </p>
        </div>

        <!-- Migration button -->
        <div class="flex items-center gap-3">
          <NextButton
            type="button"
            :label="$t('MIGRATE_CONVERSATIONS.ACTIONS.START_MIGRATION')"
            :is-loading="isLoading"
            :disabled="!selectedTargetInboxId || compatibleInboxes.length === 0"
            variant="solid"
            color="ruby"
            @click="startMigration"
          />
          <NextButton
            type="button"
            :label="$t('MIGRATE_CONVERSATIONS.ACTIONS.LOAD_STATS')"
            :is-loading="isFetchingStats"
            variant="outline"
            @click="fetchStats"
          />
        </div>
      </div>

      <!-- No data message -->
      <div v-else class="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
        <span>✅</span>
        <span>No conversations to migrate</span>
      </div>
    </div>
    </div>
  </div>
</template>
